import { IJWPlayerConfig, JWPlayerConfigEvent, VideoEventMap } from '../types'
import { JWQualityLevel } from '../types'
import { ScriptService } from 'services/script/script.service'
import { getEmbedConfig } from '../util'
import { logger } from 'utils/system/logger'
import { ComscoreAnalytics } from '../comscore-analytics'
import { StickyWrapperEvent } from 'constants/enums'
import { PlaylistStore } from '../playlist/store'
import { BrandSafety } from '../brand-safety'
import { AbstractJWPlayer } from './abstract-jw-player'

// From Analytics
const JW_INTERACTION_PLAY_REASON = ['interaction', 'related-interaction']
enum State {
    NULL = '',
    PAUSE = 'pause',
    COMPLETE = 'complete'
}
// eslint-disable-next-line no-magic-numbers
const secToMsec = 1000

export class JWPlayer extends AbstractJWPlayer {
    protected player: jwplayer.JWPlayer

    private version: string

    // From Analytics
    private playStarted = false
    private previousState: State = State.NULL
    private comscore: ComscoreAnalytics
    // When sticky container is closed before JW player initialized
    // pause action is being ignored ,causing AD to start playing outside
    // of viewport causing issues with ad provider
    // Listen to custom event from sticky component and pause offscreen video
    private asyncPauseFeaturedVideo = false
    private addWasPlayed = false
    private playerInView = false

    get index(): number {
        return this.player.getPlaylistIndex()
    }
    destroy(): void {
        this.player.remove()
    }

    public constructor(
        protected element: HTMLElement,
        protected events: JWPlayerConfigEvent = {},
        protected isLeadVideo = false
    ) {
        super(element, events, isLeadVideo)
        this.comscore = ComscoreAnalytics.getInstance()
        // this.comscore.setUpStreamingAnalytics(version)
        this.listenToSticky()
        this.iframeData = JSON.parse(this.element.dataset.evtVal || '{}')
        this.version = this.element.dataset.version || ''
    }

    /**
     * Listen and set pause flag for featured video instance only
     */
    public listenToSticky(): void {
        if (this.isFeatured) {
            window.addEventListener(StickyWrapperEvent.CLOSE, () => {
                this.asyncPauseFeaturedVideo = true
            })
            window.addEventListener(StickyWrapperEvent.MINIMIZE, () => {
                this.asyncPauseFeaturedVideo = true
            })
        }
    }

    /**
     * When the user meets the element on their screen,
     * load script and initialize the player.
     */
    public render(): void {
        ScriptService.getInstance()
            .loadScript({
                name: `jwplayer${this.version}`,
                src: `https://cdn.jwplayer.com/libraries/${this.version}.js`,
                loaded: false
            })
            .then((result) => {
                if (result.loaded) {
                    this.init().catch((err) => logger('warn', err))
                    if (this.events.onRender) {
                        this.events.onRender()
                    }
                }
            })
            .catch((error: Error) =>
                logger('error', `JW ${this.version} : ${error}`)
            )
    }

    public async init(): Promise<void> {
        const mediaEndpoint =
            this.isLeadVideo || this.isContextualPlaylist
                ? 'playlists'
                : 'media'
        const videoData = await this.getJWMediaMetadata()
        let mediaUrl = `//cdn.jwplayer.com/v2/${mediaEndpoint}/${this.videoId}`
        // Append contextual playlist specific mandatory parameter.
        if (this.isContextualPlaylist) {
            mediaUrl += '?search=__CONTEXTUAL__'
        }
        const playlistData =
            PlaylistStore.getInstance().playlist?.playlistData['items']
        let playlistConfig
        // If it's a dynamic list generated by tags, create the object needed for the playlist config
        if (
            PlaylistStore.getInstance().playlist?._propertyTag &&
            PlaylistStore.getInstance().playlist?._mainTag
        ) {
            if (playlistData) {
                playlistConfig = []
                for (const item in playlistData) {
                    const itemConfig = {
                        file: playlistData[item].source?.file,
                        image: `//cdn.jwplayer.com/v2/media/${playlistData[item].videoId}/poster.jpg`,
                        title: playlistData[item].title,
                        mediaid: playlistData[item].videoId
                    }
                    playlistConfig.push(itemConfig)
                }
            }
        } else {
            playlistConfig = mediaUrl
        }
        // Check if eager load mode (should autoplay immediately without waiting for viewable)
        const isEagerAutoplay =
            this.getAutoplay() &&
            this.element.closest('[data-video-eager-load]')

        let config: IJWPlayerConfig = {
            playlist: playlistConfig,
            width: '100%',
            aspectratio: '16:9',
            autostart: isEagerAutoplay
                ? true
                : this.getAutoplay()
                ? 'viewable'
                : false,
            controls: this.controls,
            mute: isEagerAutoplay ? true : this.mute,
            repeat: this.loop,
            mediaid: this.videoId
        }

        if (this.isMonetize(videoData)) {
            this.embedConfig = await getEmbedConfig()

            if (Object.keys(this.embedConfig).length) {
                config = {
                    ...config,
                    advertising: this.getAdvertisingConfigData(
                        this.embedConfig,
                        undefined,
                        this.loc
                    )
                }
            }
        }

        this.player = window.jwplayer(this.element)

        if (!Object.prototype.hasOwnProperty.call(this.player, 'setup')) {
            return Promise.reject()
        }

        this.player.setup(config)

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.player.on('viewable', (event) => {
            if ('viewable' in event) {
                this.playerInView = !!event.viewable
            }
        })

        this.player.on('ready', () => {
            this.onReady()
            if (this.events.onReady) {
                this.events.onReady()
            }
            const currentItem = this.player.getPlaylistItem()
            if (currentItem && currentItem.wire_service) {
                this.iframeData['wire_service'] = currentItem.wire_service
            } else if (this.iframeData['wire_service']) {
                delete this.iframeData['wire_service']
            }
        })
        this.player.on('meta', () => {
            const currentItem = this.player.getPlaylistItem()
            if (currentItem && currentItem.wire_service) {
                this.iframeData['wire_service'] = currentItem.wire_service
            } else if (this.iframeData['wire_service']) {
                delete this.iframeData['wire_service']
            }
        })
        this.player.on('beforePlay', () => this.onBeforePlay())
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.player.on('play', (event: any) => {
            if (this.asyncPauseFeaturedVideo && !this.playerInView) {
                this.player.stop()
                this.asyncPauseFeaturedVideo = false
                return
            }

            this.onPlay(event)
            if (this.events.onPlay) {
                this.events.onPlay(event)
            }
        })
        this.player.on('playlistItem', (event: jwplayer.PlaylistItemParam) =>
            this.onPlaylistItem(event)
        )
        this.player.on('mute', (event: jwplayer.MuteParam) =>
            this.onMute(event)
        )
        this.player.on('buffer', () => this.onBuffer())
        this.player.on('firstFrame', () => this.onFirstFrame())
        this.player.on('beforeComplete', () => this.onBeforeComplete())
        this.player.on('complete', () => this.onComplete())
        this.player.on('pause', () => {
            this.onPause()
            if (this.events.onPause) {
                this.events.onPause()
            }
        })
        this.player.on('seek', () => this.onSeek())
        this.player.on('seeked', () => this.onSeeked())
        this.player.on('error', (event: jwplayer.ErrorParam) => {
            this.onError(event)
            if (this.events.onError) {
                this.events.onError(event)
            }
        })
        // the types library only has API calls up to version 8.2 (2018)
        // the on('adLoaded') call was added in JWPlayer 8.17 (Aug 2020)
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.player.on('adLoaded', (event: unknown) => {
            this.onAdLoaded(event)
            if (this.asyncPauseFeaturedVideo && !this.addWasPlayed) {
                this.player.pauseAd(true)
            }
        })
        this.player.on('adStarted', () => {
            this.onAdStarted()
            if (this.asyncPauseFeaturedVideo && !this.addWasPlayed) {
                this.player.pauseAd(true)
            }
            this.addWasPlayed = true
        })
        this.player.on('adPlay', (event: jwplayer.AdPlayParam) =>
            this.onAdPlay(event)
        )
        this.player.on('adPause', () => this.onAdPause())
        this.player.on('adComplete', () => this.onAdEnd())
        this.player.on('adSkipped', () => this.onAdEnd())
        this.player.on('setupError', (event) => {
            this.onError(event)
            if (this.events.onError) {
                this.events.onError(event)
            }
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.player.on('visualQuality', (event: any) =>
            this.onQualityChange(event)
        )
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.player.on('playbackRateChanged', (event: any) =>
            this.onPlaybackRateChange(event)
        )
        this.player.on('adsManager', async (event: any) =>
            this.onAdsManager(event)
        )
        return Promise.resolve()
    }

    protected onReady(): void {
        this.isVolumeEnabled = !this.player.getMute()
    }

    private onBeforePlay(): void {
        // if this beforePlay event corresponds to a new video starting:
        if (this.comscore.beforePlay()) {
            const item = this.player.getPlaylistItem()
            this.comscore.specifyContentMetadata(
                this.videoId,
                this.videoTitle,
                item.feedData.title,
                item.tags
                    .split(',')
                    .filter((tag: string) => tag.indexOf('@') == 0)[0]
                    .slice(1),
                this.player.getDuration() * secToMsec
            )
        }
    }

    private onBeforeComplete(): void {
        this.comscore.beforeComplete()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    private onPlay(event: any): void {
        if (this.type === 'hero') {
            this.player.getContainer().tabIndex = -1
        }
        this.pauseAllOther()
        const qualityLevel =
            (this.player.getVisualQuality() as JWQualityLevel) || {
                level: { index: 0 }
            }
        this.playbackQuality = this.getVisualQuality(qualityLevel.level.index)

        if (!this.playStarted && this.previousState != event.playReason) {
            const isNonInteraction = !JW_INTERACTION_PLAY_REASON.includes(
                event.playReason
            )
                ? true
                : false
            // keep same autoPlay state if previousState is pause. Otherwise, isAutoPlay same as isNonInteraction
            if (this.previousState !== State.PAUSE) {
                this.autoplay = isNonInteraction
            }
            this.previousState = event.reason
            this.dispatchAnalyticsPlay(isNonInteraction)
        }
        if (event.oldstate === 'buffering') {
            this.comscore.notifyBufferStop()
        }
        this.comscore.notifyPlay()
        this.playStarted = true
    }

    private onPlaylistItem(event: jwplayer.PlaylistItemParam): void {
        if (event.item && event.item.mediaid) {
            this.videoId = event.item['mediaid']
            this.videoTitle = event.item['title']
            if (event.item['monetize'] === false) {
                // skip ad
                this.player.skipAd()
            }
            this.resetFlags()
        }

        this.comscore.newPlaylistItem()

        const eventC = new CustomEvent(VideoEventMap.JW_SKIP, {
            detail: event.item
        })
        window.dispatchEvent(eventC)
    }

    private onMute(event: jwplayer.MuteParam): void {
        this.isVolumeEnabled = !event.mute
    }

    private onBuffer(): void {
        this.comscore.notifyBufferStart()
    }

    private onFirstFrame(): void {
        this.comscore.firstFrame()
    }

    private onComplete(): void {
        this.playStarted = false
        this.previousState = State.COMPLETE
        this.comscore.notifyEnd()
        this.dispatchAnalyticsComplete()
    }

    private onPause(): void {
        this.playStarted = false
        this.previousState = State.PAUSE
        this.comscore.notifyPause()
        this.dispatchAnalyticsPaused()
    }

    private onSeek(): void {
        this.comscore.notifySeekStart()
    }

    private onSeeked(): void {
        this.comscore.startFromPosition(this.player.getPosition() * secToMsec)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
    private onAdLoaded(event: any): void {
        this.comscore.setAdvertisingMetadata(event.duration * secToMsec)
    }

    private onAdStarted(): void {
        this._isAdPLaying = true
        this.comscore.notifyPlay()
    }

    private onAdPlay(event: jwplayer.AdPlayParam): void {
        // All content begins with a pre-roll
        this.comscore.notifyPlay()
        logger(
            'warn',
            'Fired whenever an ad starts playing or when an ad is unpaused.',
            event
        )
        this.pauseAllOther()
        document.dispatchEvent(new Event('videoExternalPlayerPlayed'))
    }

    private onAdPause(): void {
        this._isAdPLaying = false
        this.comscore.notifyPause()
    }

    private async onAdsManager(event: any): Promise<void> {
        await BrandSafety.onAdsManagerLoaded(event, this.embedConfig)
    }

    protected onAdEnd(): void {
        this._isAdPLaying = false
        super.onAdEnd()
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
    private onQualityChange(event: any): void {
        this.playbackQuality = this.getVisualQuality(event.level.index)
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-unused-vars
    private onPlaybackRateChange(event: any): void {
        this.comscore.notifyChangePlaybackRate(event.playbackRate)
    }

    public get title(): string {
        return this.player.getPlaylistItem()['title']
    }
    public pause(): void {
        if (this.player && this.getState() == 'playing') {
            this.player.pause()
        }
    }
    public play(): void {
        // Do not play if sticky component was closed and dispatched custom pause event
        if (this.asyncPauseFeaturedVideo) {
            return
        }
        if (this.player && typeof this.player !== 'undefined') {
            this.player.play()
        }
    }
    public playVideoAt(index: number): void {
        if (this.player && typeof this.player !== 'undefined') {
            this.player.playlistItem(index)
        }
    }
    public setMute(mute: boolean): void {
        if (this.player && typeof this.player !== 'undefined') {
            this.player.setMute(mute)
        }
    }
    public setControls(controls: boolean): void {
        if (this.player && typeof this.player !== 'undefined') {
            this.player.setControls(controls)
        }
    }
    public setLoop(loop: boolean): void {
        if (this.player && typeof this.player !== 'undefined') {
            // JW API has setConfig but type definition doesn't
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(this.player as any).setConfig({
                repeat: loop
            })
        }
    }
    public getAutoplay(): boolean {
        return this.autoplay
    }
    public setAutoplay(autoplay: boolean): void {
        if (this.player && typeof this.player !== 'undefined') {
            // JW API has setConfig but type definition doesn't
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ;(this.player as any).setConfig({
                autostart: autoplay ? 'viewable' : false
            })
        }
    }
    public isPausable(): boolean {
        return this.getAutoplay()
    }

    public getState(): string {
        if (!this.player.getState) {
            return 'idle'
        }

        const state = this.player.getState()
        if (state == 'paused') {
            return 'paused'
        }
        if (state == 'playing') {
            return 'playing'
        }
        return 'idle'
    }

    /**
     * get Current Visual Quality
     */
    private getVisualQuality(index = 0): string {
        const levels = this.player.getQualityLevels()
        return levels[index].label
    }

    // From Analytics
    /**
     * Reset Flags should be called with new video
     */
    public resetFlags(): void {
        this.playStarted = false
        this.previousState = State.NULL
    }

    /**
     * Handle the player error event
     *
     * @param event
     */
    public onError(event: jwplayer.ErrorParam): void {
        if (event.message) {
            this.dispatchAnalyticsError(`${event.message} code: ${event.code}`)
        }
    }
}
